import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Table, 
  Card, 
  Spinner, 
  Alert, 
  Container, 
  Button,
  Modal,
  Form,
  Badge,
  Row,
  Col
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { ppeDepartmentSettingsAPI } from '../utils/api';
import type { PPEDepartmentRequirement } from '../types/ppe';

const isDev = import.meta.env.DEV;
const logError = (message: string, ...args: any[]) => {
  if (isDev) console.error(message, ...args);
};

const PPE_TYPE_IDS = ['hairnet', 'gloves', 'helmet', 'industrial_gloves', 'industrial_jacket'] as const;

const PPE_TYPE_I18N_KEYS: Record<string, string> = {
  hairnet: 'ppeTypes.hairnet',
  gloves: 'ppeTypes.gloves',
  helmet: 'ppeTypes.helmet',
  industrial_gloves: 'ppeTypes.industrialGloves',
  industrial_jacket: 'ppeTypes.industrialJacket',
};


const PPEDepartmentSettings: React.FC = () => {
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const { canAccessPpeSettings, canEditPpeSettings } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'ppeDepartmentSettings' });
  const { t: tc } = useTranslation('common');

  const ppeTypes = useMemo(() =>
    PPE_TYPE_IDS.map(id => ({ id, label: tc(PPE_TYPE_I18N_KEYS[id]) })),
    [tc]
  );

  const getPpeTypeLabel = useCallback((type: string) => (
    ppeTypes.find(pt => pt.id === type)?.label || type
  ), [ppeTypes]);

  const [requirements, setRequirements] = useState<PPEDepartmentRequirement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ppeDisabled, setPpeDisabled] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState<PPEDepartmentRequirement | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  
  const [selectedPpeTypes, setSelectedPpeTypes] = useState<string[]>([]);
  const [strictMode, setStrictMode] = useState(false);
  const [ppeStatus, setPpeStatus] = useState(false);

  const enabledCount = useMemo(() => requirements.filter(req => req.ppe_status).length, [requirements]);
  const strictCount = useMemo(() => requirements.filter(req => req.ppe_status && req.strict_mode).length, [requirements]);
  const overrideCount = useMemo(() => requirements.filter(req => req.ppe_status && req.source === 'department').length, [requirements]);

  const fetchRequirements = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setLoading(true);
      setError(null);
      setPpeDisabled(false);
      
      const response = await ppeDepartmentSettingsAPI.listDepartmentPpeRequirements(currentCompany.company_id);
      setRequirements(response.data || []);
    } catch (err: any) {
      logError('Error fetching PPE requirements:', err);
      if (err.response?.status === 403) {
        setPpeDisabled(true);
      } else {
        setError(err.response?.data?.message || err.message || t('fetchFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [currentCompany, t]);

  useEffect(() => {
    if (canAccessPpeSettings) {
      fetchRequirements();
    }
  }, [fetchRequirements, canAccessPpeSettings]);

  const handleEdit = (dept: PPEDepartmentRequirement) => {
    setEditingDept(dept);
    setSelectedPpeTypes(dept.required_ppe_types || []);
    setStrictMode(dept.strict_mode);
    setPpeStatus(dept.ppe_status);
    setShowModal(true);
    setModalError(null);
  };

  const handleTogglePpeType = (typeId: string) => {
    setSelectedPpeTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId) 
        : [...prev, typeId]
    );
  };

  const handlePpeStatusChange = (checked: boolean) => {
    setPpeStatus(checked);
    if (!checked) {
      setSelectedPpeTypes([]);
      setStrictMode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCompany || !editingDept) return;

    try {
      setModalLoading(true);
      setModalError(null);
      
      const payload = ppeStatus ? {
        required_ppe_types: selectedPpeTypes,
        strict_mode: strictMode,
        ppe_status: true
      } : {
        required_ppe_types: null,
        strict_mode: false,
        ppe_status: false
      };
      
      await ppeDepartmentSettingsAPI.updateDepartmentPpeRequirements(
        currentCompany.company_id,
        editingDept.department_id,
        payload
      );
      
      await fetchRequirements();
      setShowModal(false);
    } catch (err: any) {
      setModalError(err.response?.data?.message || err.message || t('updateFailed'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!currentCompany || !editingDept) return;

    try {
      setModalLoading(true);
      setModalError(null);
      
      await ppeDepartmentSettingsAPI.updateDepartmentPpeRequirements(
        currentCompany.company_id,
        editingDept.department_id,
        {
          required_ppe_types: null,
          strict_mode: false,
          ppe_status: true
        }
      );
      
      await fetchRequirements();
      setShowModal(false);
    } catch (err: any) {
      setModalError(err.response?.data?.message || err.message || t('resetFailed'));
    } finally {
      setModalLoading(false);
    }
  };

  const renderPpeBadges = (req: PPEDepartmentRequirement) => {
    if (!req.ppe_status) {
      return <span className="text-muted fw-semibold">{tc('status.disabled')}</span>;
    }

    if (req.source === 'company') {
      return <Badge bg="secondary" className="an-ppe-chip">{t('companyDefault')}</Badge>;
    }

    const selectedTypes = req.required_ppe_types || [];
    if (selectedTypes.length === 0) {
      return <span className="text-muted fw-semibold">{t('companyDefault')}</span>;
    }

    return selectedTypes.map(type => (
      <Badge key={type} bg="info" className="an-ppe-chip">
        {getPpeTypeLabel(type)}
      </Badge>
    ));
  };


    if (userRolePermissionsLoading || loading) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page an-ppe-settings-page">
          <div className="page-header">
            <h2 className="an-page-title">{t('title')}</h2>
          </div>
          <div className="py-5 text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </Layout>
    );
  }

  if (!canAccessPpeSettings) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page an-page-shell an-data-page an-ppe-page">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page an-ppe-page an-ppe-settings-page">
        <div className="page-header">
          <h2 className="an-page-title">{t('title')}</h2>
          <p className="an-page-subtitle">{t('subtitle')}</p>
        </div>

        {ppeDisabled ? (
          <Alert variant="warning">
            {t('ppeNotEnabled')}
          </Alert>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <>
            <Row className="g-3 mb-4 an-ppe-summary-row">
              <Col sm={6} xl={3}>
                <div className="an-ppe-summary-tile">
                  <div className="an-ppe-summary-icon"><i className="bi bi-diagram-3"></i></div>
                  <div><div className="an-ppe-summary-label">{t('departmentName')}</div><div className="an-ppe-summary-value">{requirements.length}</div></div>
                </div>
              </Col>
              <Col sm={6} xl={3}>
                <div className="an-ppe-summary-tile">
                  <div className="an-ppe-summary-icon is-success"><i className="bi bi-shield-check"></i></div>
                  <div><div className="an-ppe-summary-label">{t('ppeStatusLabel')}</div><div className="an-ppe-summary-value">{enabledCount}</div></div>
                </div>
              </Col>
              <Col sm={6} xl={3}>
                <div className="an-ppe-summary-tile">
                  <div className="an-ppe-summary-icon is-danger"><i className="bi bi-exclamation-octagon"></i></div>
                  <div><div className="an-ppe-summary-label">{t('strictModeLabel')}</div><div className="an-ppe-summary-value">{strictCount}</div></div>
                </div>
              </Col>
              <Col sm={6} xl={3}>
                <div className="an-ppe-summary-tile">
                  <div className="an-ppe-summary-icon is-info"><i className="bi bi-sliders"></i></div>
                  <div><div className="an-ppe-summary-label">{t('sourceLabel')}</div><div className="an-ppe-summary-value">{overrideCount}</div></div>
                </div>
              </Col>
            </Row>

            <Card className="an-data-card an-ppe-settings-card">
              <Card.Header className="an-section-divider an-ppe-settings-card-header">
                <div>
                  <div className="an-card-kicker">{t('requiredPpeTypes')}</div>
                  <h5 className="mb-0">{t('title')}</h5>
                </div>
                <Badge bg="light" text="dark" className="border an-ppe-count-badge">{requirements.length}</Badge>
              </Card.Header>
            <Card.Body className="p-0 an-data-card">
              {/* Mobile View */}
              <div className="d-md-none p-3">
                {requirements.map((req) => (
                  <Card key={req.department_id} className="mb-3 shadow-sm border an-data-card an-ppe-mobile-card">
                    <Card.Body className="an-data-card">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center gap-2 min-w-0"><span className="an-ppe-department-icon"><i className="bi bi-diagram-3"></i></span><h6 className="fw-bold mb-0 text-truncate">{req.department_name}</h6></div>
                        {canEditPpeSettings && (
                          <Button variant="link" size="sm" className="text-primary p-1 text-decoration-none" onClick={() => handleEdit(req)}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
                        )}
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">{t('ppeStatusLabel')}:</small>
                        <Badge bg={req.ppe_status ? 'success' : 'secondary'} className="me-1">
                          {req.ppe_status ? tc('status.enabled') : tc('status.disabled')}
                        </Badge>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">{t('requiredPpe')}:</small>
                        {req.source === 'company' ? (
                          <Badge bg="secondary">{t('companyDefault')}</Badge>
                        ) : (
                          (req.required_ppe_types || []).map(type => (
                            <Badge key={type} bg="info" className="me-1">
                              {ppeTypes.find(pt => pt.id === type)?.label || type}
                            </Badge>
                          ))
                        )}
                      </div>
                      <div className="an-ppe-mobile-grid">
                        <div>
                          <small className="text-muted d-block">{t('strictModeLabel')}:</small>
                          <Badge bg={req.strict_mode ? 'danger' : 'success'} className="an-ppe-chip" title={t('strictModeTooltip')}>
                            {req.strict_mode ? t('on') : t('off')}
                          </Badge>
                        </div>
                        <div>
                          <small className="text-muted d-block">{t('sourceLabel')}:</small>
                          <Badge bg={req.source === 'department' ? 'primary' : 'secondary'} className="an-ppe-chip">
                            {req.source}
                          </Badge>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
                {requirements.length === 0 && <div className="text-center py-4">{t('noDepartments')}</div>}
              </div>

              {/* Desktop View */}
              <div className="d-none d-md-block">
                <div className="table-responsive an-data-table an-ppe-settings-table">
                  <Table hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>{t('departmentName')}</th>
                      <th>{t('ppeStatusLabel')}</th>
                      <th>{t('requiredPpeTypes')}</th>
                      <th>{t('strictModeLabel')}</th>
                      <th>{t('sourceLabel')}</th>
                      <th>{tc('labels.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requirements.map((req) => (
                      <tr key={req.department_id}>
                        <td className="fw-semibold"><span className="an-ppe-department-cell"><span className="an-ppe-department-icon"><i className="bi bi-diagram-3"></i></span>{req.department_name}</span></td>
                        <td>
                          <Badge bg={req.ppe_status ? 'success' : 'secondary'} className="an-ppe-chip">
                            {req.ppe_status ? tc('status.enabled') : tc('status.disabled')}
                          </Badge>
                        </td>
                        <td><div className="d-flex flex-wrap gap-2">{renderPpeBadges(req)}</div></td>
                        <td>
                          {req.ppe_status && <Badge bg={req.strict_mode ? 'danger' : 'success'} className="an-ppe-chip" title={t('strictModeTooltip')}>
                            {req.strict_mode ? t('on') : t('off')}
                          </Badge>}
                        </td>
                        <td>
                          {req.ppe_status && <Badge bg={req.source === 'department' ? 'primary' : 'secondary'} className="an-ppe-chip">
                            {req.source}
                          </Badge>}
                        </td>
                        <td>
                          {canEditPpeSettings && (
                            <Button variant="link" size="sm" className="text-primary p-1 text-decoration-none" onClick={() => handleEdit(req)}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {requirements.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-4">{t('noDepartments')}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card.Body>
        </Card>
          </>
      )}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" dialogClassName="an-ppe-modal">
          <Modal.Header closeButton className="an-section-divider">
            <Modal.Title>
              <span className="an-ppe-modal-title-icon me-2"><i className="bi bi-shield-check"></i></span>
              {t('editRequirementsTitle', { name: editingDept?.department_name })}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}
              
              <div className="an-ppe-config-panel mb-4">
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <div className="an-card-kicker">{t('ppeStatusLabel')}</div>
                    <h6 className="mb-1">{t('enablePpe')}</h6>
                    <Form.Text className="text-muted">{t('enablePpeHint')}</Form.Text>
                  </div>
                  <Form.Check
                    type="switch"
                    id="ppe-status-switch"
                    checked={ppeStatus}
                    onChange={(e) => handlePpeStatusChange(e.target.checked)}
                    className="an-modern-switch"
                    aria-label={t('enablePpe')}
                  />
                </div>
              </div>

              <Form.Group className="mb-4">
                <div className="d-flex justify-content-between align-items-end gap-3 mb-3">
                  <div>
                    <div className="an-card-kicker">{t('requiredPpe')}</div>
                    <Form.Label className="fw-bold mb-0">{t('requiredPpeTypes')}</Form.Label>
                  </div>
                  <Badge bg={ppeStatus ? 'primary' : 'secondary'} className="an-ppe-chip">
                    {selectedPpeTypes.length}
                  </Badge>
                </div>
                <Row className="g-3 an-ppe-type-grid">
                  {ppeTypes.map(type => {
                    const checked = selectedPpeTypes.includes(type.id);
                    return (
                      <Col sm={6} lg={4} key={type.id}>
                        <label className={`an-ppe-type-option ${checked ? 'is-selected' : ''} ${!ppeStatus ? 'is-disabled' : ''}`} htmlFor={`ppe-type-${type.id}`}>
                          <Form.Check
                            type="checkbox"
                            id={`ppe-type-${type.id}`}
                            label=""
                            checked={checked}
                            onChange={() => handleTogglePpeType(type.id)}
                            disabled={!ppeStatus}
                            className="an-ppe-type-check"
                          />
                          <span className="an-ppe-type-icon"><i className="bi bi-shield-check"></i></span>
                          <span className="an-ppe-type-label">{type.label}</span>
                        </label>
                      </Col>
                    );
                  })}
                </Row>
              </Form.Group>

              <div className={`an-ppe-config-panel ${!ppeStatus ? 'is-disabled' : ''}`}>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <div className="an-card-kicker">{t('strictModeLabel')}</div>
                    <h6 className="mb-1">{t('strictModeLabel')}</h6>
                    <Form.Text className="text-muted">{t('strictModeHint')}</Form.Text>
                  </div>
                  <Form.Check
                    type="switch"
                    id="strict-mode-switch"
                    checked={strictMode}
                    onChange={(e) => setStrictMode(e.target.checked)}
                    className="an-modern-switch"
                    disabled={!ppeStatus}
                    aria-label={t('strictModeLabel')}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-between an-section-divider">
              <Button variant="outline-secondary" onClick={handleResetToDefault} disabled={modalLoading}>
                <i className="bi bi-arrow-counterclockwise"></i> {t('resetToDefault')}
              </Button>
              <div className="d-flex gap-2">
                <Button variant="secondary" onClick={() => setShowModal(false)} disabled={modalLoading}>
                  {tc('buttons.cancel')}
                </Button>
                <Button variant="primary" type="submit" disabled={modalLoading}>
                  {modalLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-save"></i>}
                  <span>{t('saveChanges')}</span>
                </Button>
              </div>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Layout>
  );
};

export default PPEDepartmentSettings;





